import { useCallback, useMemo, useState, type VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { plural } from '../../../../shared/helpers';
import { useDresser } from '../../../contexts/DrawerContext';
import { useParams } from '../../../contexts/RouterContext';
import { useDebouncedInputValue } from '../../../hooks';
import { useVendoredDependencies } from '../../../hooks/api/dependencies/useVendoredDependencies';
import { queryClient } from '../../../ReactQuery';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import { convertSearchByLocatorQuery } from '../../../redux/dependenciesV2/actions';
import AddDependencyDrawer from '../../drawers/AddDependencyDrawer';
import { SearchInput } from '../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import { useDependencyDrawer } from '../dependenciesV2/hooks/useDependencyDrawer';
import { useSetDependencyScope } from '../dependenciesV2/hooks/useSetDependenciesScope';
import AddDependency from '../dependenciesV2/modals/AddDependency';
import RemoveDependency, { type DependencyToRemove } from '../dependenciesV2/modals/RemoveDependency';
import StreamBuild from '../dependenciesV2/modals/StreamBuild';
import { TreeViewLayout } from '../shared/TreeViewLayout';
import type { ManuallyAddedDependencyRequestKind } from '../../../../typings/api';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import VendoredDependenciesTable from './Table';
import VendoredDependenciesTreeView from './TreeView';
import { useSelectedPath } from './useSelectedPath';
import { summarizePath } from './utils';

import styles from './VendoredDependencies.module.less';

/**
 * Root component for the Vendored Dependencies page.
 * Includes only the content of the page, the SidebarNavLayout is handled in the parent route.
 */
const VendoredDependenciesContainer: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);

  const { pushDrawer } = useDresser();

  // used by the dependency drawer
  const [selectedDependencyRevisionLocator, setSelectedDependencyRevisionLocator] = useState<string | null>(null);
  // used by the delete dependency component
  const [dependencyToRemove, setDependencyToRemove] = useState<DependencyToRemove | null>(null);
  // used to overwrite user dependencies
  const [dependencyLocatorToOverwrite, setDependencyLocatorToOverwrite] = useState<string | null>(null);
  // used by the add dependency component
  const [manualDependencyAddMode, setManualDependencyAddMode] = (
    useState<ManuallyAddedDependencyRequestKind | null>(null));
    // used to view in progress builds
  const [inProgressBuildId, setInProgressBuildId] = useState<number | null>(null);

  // Track which path is currently selected
  const { selectedPath, setSelectedPath } = useSelectedPath();
  //  Note that the user is allowed to filter by locator or title.
  const { value, debouncedValue, onChange } = useDebouncedInputValue();

  //  This method will convert the `title` query param to a `locators` query param if it seems to be a locator.
  //  This is so that users can search by locator or title.
  const getDepsQuery = useMemo(() => convertSearchByLocatorQuery({
    title: debouncedValue?.trim() || undefined,
  }), [debouncedValue]);

  //  Fetch the vendored dependencies and the total count.
  //  The total count is used in a few different places higher up the component tree,
  //  which is why we don't fetch it down in the VendoredDependenciesTable component.
  const { data, isFetching, error } = useVendoredDependencies(userRevisionLocator, selectedPath, getDepsQuery);
  const dependencies = data?.dependencies ?? [];
  const totalCount = data?.total ?? 0;

  const onRemoveProjectVendoredDependency = useCallback(() => {
    setSelectedDependencyRevisionLocator(null);
    setDependencyToRemove(null);
    queryClient.invalidateQueries({
      queryKey: ['revisions', userRevisionLocator, 'dependencies'],
    });
  }, [userRevisionLocator]);

  useDependencyDrawer({
    scope,
    selectedDependencyRevisionLocator,
    setSelectedDependencyRevisionLocator,
    onDeleteProjectDependency: onRemoveProjectVendoredDependency,
  });

  useSetDependencyScope({
    projectId: params.project_id,
    revisionId: params.revision_id,
  });

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>
          Vendored Dependencies
        </h1>
        <span className={styles.summary}>
          {totalCount} vendored {plural(data?.total ?? 0, 'dependency', 'dependencies')} found in <span className="bold">{summarizePath(selectedPath ?? '/')}</span>
        </span>
      </header>
      <div className={styles.subHeader}>
        <span className={styles.search}>
          <SearchInput
            label="vendored-dependencies-filter-search"
            placeholder="Filter by name or locator"
            size="small"
            value={value}
            onChange={onChange}
          />
        </span>
        <Button
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.LARGE}
          onClick={() => pushDrawer(<AddDependencyDrawer userRevisionLocator={userRevisionLocator} isVendored />)}
        >
          Add Dependency
        </Button>
      </div>
      <TreeViewLayout>
        <VendoredDependenciesTreeView
          userRevisionLocator={userRevisionLocator}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
        />
        {/* TODO: this will be extracted into a component later */}
        <TreeViewLayout.Table
          header={(
            <span className={styles.tableHeader}>
              Components <SubnavCountBadge count={totalCount} isLoading={isFetching} />
            </span>
          )}
        >
          <VendoredDependenciesTable
            isLoading={isFetching}
            error={error}
            dependencies={dependencies}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
            setDependencyToRemove={setDependencyToRemove}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setInProgressBuildId={setInProgressBuildId}
          />
        </TreeViewLayout.Table>
      </TreeViewLayout>
      <StreamBuild buildId={inProgressBuildId} setBuildId={setInProgressBuildId} />
      {scope && scope.scope === 'project' && (
        <>
          <AddDependency
            userRevisionLocator={scope.revision}
            manualDependencyAddMode={manualDependencyAddMode}
            setManualDependencyAddMode={setManualDependencyAddMode}
            dependencyLocatorToOverwrite={dependencyLocatorToOverwrite}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
          />
          <RemoveDependency
            userRevisionLocator={scope.revision}
            dependencyToRemove={dependencyToRemove}
            setDependencyToRemove={setDependencyToRemove}
            onRemoveProjectDependency={onRemoveProjectVendoredDependency}
        />
        </>
      )}
    </>
  );
};

export default VendoredDependenciesContainer;
